/**
 * PerpetualMotionSkillEffect — Specter wraith-path Endurance upgrade: dash refill gate override.
 *
 * Upgrade enhancing Surge (via Deep Reserves). Permanently overrides the
 * "dash charges only refill when stamina is full" gate, allowing charges
 * to refill regardless of current stamina level. This is the system-breaking
 * upgrade — once unlocked, the stamina-dash link is severed.
 *
 * On equip: sets `overrideRefillRequiresFullStamina = false` on DashComponent.
 * On unequip: resets `overrideRefillRequiresFullStamina` to null (restore config default).
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see specter-drift-path-proposal.md § Perpetual Motion
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface PerpetualMotionDeps {
  readonly dash: {
    /** When non-null, overrides config refillRequiresFullStamina gate. */
    overrideRefillRequiresFullStamina: boolean | null;
  };
}

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class PerpetualMotionSkillEffect implements SkillEffect {
  readonly skillId = 'perpetual_motion';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;
  readonly staminaCost: number = 0;

  private readonly deps: PerpetualMotionDeps;

  /** Whether the override has been applied (guards against double-application). */
  private _applied = false;

  constructor(deps: PerpetualMotionDeps) {
    this.deps = deps;
  }

  // --- SkillEffect interface ---

  canActivate(): boolean { return false; }
  activate(): boolean { return false; }
  deactivate(): void {}
  isActive(): boolean { return false; }

  update(_delta: number): void {
    // Apply the dash refill override on first update tick (equip).
    // Done here rather than constructor to match the pattern where
    // deps are guaranteed initialized by the first update call.
    if (!this._applied) {
      this.deps.dash.overrideRefillRequiresFullStamina = false;
      this._applied = true;
    }
  }

  destroy(): void {
    // Reset to null (restore config default) on unequip
    if (this._applied) {
      this.deps.dash.overrideRefillRequiresFullStamina = null;
      this._applied = false;
    }
  }
}
